/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';

import { FETCHER_STATES } from '@/lib/fetcher-states';
import { resourceRoutes } from '@/route-paths';

/**
 * Custom hook for managing the donation add/replace/remove flow via a React
 * Router fetcher — mirrors `usePromoCodeActions`.
 *
 * @param basketId - Basket ID to associate the donation mutation with
 *
 * @example
 * ```tsx
 * const { saveDonation, fetcher } = useDonationActions(basketId);
 *
 * saveDonation(25); // add/replace donation
 * saveDonation(0);  // remove donation
 *
 * if (fetcher.state === FETCHER_STATES.SUBMITTING) {
 *   // Show loading state
 * }
 * ```
 */
export function useDonationActions(basketId?: string) {
    const { t } = useTranslation();
    const fetcher = useFetcher();

    /**
     * Adds, replaces, or removes (when `amount <= 0`) the basket's donation line item.
     *
     * @throws Error if no basket ID is provided
     */
    const saveDonation = (amount: number) => {
        if (!basketId) {
            throw new Error(t('errors:noBasketFound'));
        }

        // Prevent concurrent submits (explicit confirm-once flow, not per-keystroke).
        if (fetcher.state === FETCHER_STATES.SUBMITTING) {
            return;
        }

        void fetcher.submit(
            {
                amount: String(amount),
            },
            {
                method: 'POST',
                action: resourceRoutes.cartDonationUpdate,
            }
        );
    };

    return {
        saveDonation,
        fetcher,
    };
}
